
import { Facebook, Twitter, Instagram, Linkedin, Youtube, Mail, MapPin } from "lucide-react";
import type { FunnelData } from "../types";
import { cn } from "@/lib/utils";
import { LogoDisplay } from "./LogoDisplay";

interface PreviewFooterProps {
  data: FunnelData;
  colors: any;
}

export const PreviewFooter = ({ data, colors }: PreviewFooterProps) => {
  const socialIcons: Record<string, React.ComponentType<{ className?: string }>> = {
    facebook: Facebook,
    twitter: Twitter,
    instagram: Instagram,
    linkedin: Linkedin,
    youtube: Youtube,
    email: Mail,
    map: MapPin,
  };

  const enabledSocialLinks = data.socialMedia?.filter(link => link.enabled) || [];

  return (
    <footer 
      className={cn(
        "mt-auto py-8 px-6 relative",
        "transition-all duration-300"
      )}
      style={{ 
        background: colors.glass,
        borderTop: `1px solid ${colors.primary}20`,
      }}
    >
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="space-y-4">
            <LogoDisplay data={data} colors={colors} />
            <p className="text-sm opacity-80" style={{ color: colors.primary }}>
              Building better funnels for your business
            </p>
          </div>
          
          <div className="space-y-4">
            <h3 className="font-semibold text-lg" style={{ color: colors.primary }}>
              Quick Links
            </h3>
            <nav className="flex flex-col space-y-2">
              {data.sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="text-sm hover:opacity-80 transition-opacity"
                  style={{ color: colors.primary }}
                >
                  Section {section.id.slice(0, 4)}
                </a>
              ))}
            </nav>
          </div>

          {enabledSocialLinks.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg" style={{ color: colors.primary }}>
                Connect
              </h3>
              <div className="flex gap-4">
                {enabledSocialLinks.map((link) => {
                  const Icon = socialIcons[link.platform];
                  if (!Icon) return null;
                  
                  return (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-8 w-8 rounded-full hover:bg-white/20 transition-all flex items-center justify-center"
                      style={{ color: colors.primary }}
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t"
             style={{ borderColor: `${colors.primary}20` }}>
          <p className="text-sm" style={{ color: colors.primary }}>
            © {new Date().getFullYear()} {data.siteName}. All rights reserved.
          </p>
       
          {data.showBadge && (
            <div className="mt-4 md:mt-0">
              <a
                href="#"
                className={cn(
                  "inline-flex items-center gap-2 px-3 py-1 text-sm rounded-full",
                  "transition-all hover:bg-white/20"
                )}
                style={{ 
                  background: `${colors.primary}20`,
                  color: colors.primary
                }}
              >
                Made with Funnel Builder
              </a>
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};
